import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, Loader2 } from "lucide-react";
import { trackSignUp, trackStartTrial } from "@/lib/analytics";

export default function SubscribeSuccessPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user, completeSignup, refresh } = useAuth();
  const [state, setState] = useState("polling"); // polling | ready | timeout | error
  const [message, setMessage] = useState("");
  const attemptsRef = useRef(0);
  const doneRef = useRef(false);
  // Guard against concurrent tick() invocations while an async request is
  // still in-flight. Without this, /auth/complete-signup gets called twice
  // and the second call fails (pending_signup already consumed) leaving the
  // page stuck on "Stiamo creando il tuo account…".
  const inflightRef = useRef(false);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) {
      setState("timeout");
      return;
    }

    let cancelled = false;
    let interval = null;
    let redirectTimer = null;

    const finish = () => {
      if (cancelled) return;
      doneRef.current = true;
      setState("ready");
      if (interval) clearInterval(interval);
      // Auto-navigate to the editor so the user isn't stranded on the
      // success page. Small delay lets the confirmation flash briefly.
      redirectTimer = setTimeout(() => {
        if (!cancelled) nav("/", { replace: true });
      }, 900);
    };

    const tick = async () => {
      if (doneRef.current || cancelled || inflightRef.current) return;
      inflightRef.current = true;
      attemptsRef.current += 1;
      try {
        if (!user) {
          // Fresh signup: user is not logged in yet. Finalize the account.
          const created = await completeSignup(sid);
          // GA4 sign_up + revo_start_trial_confirmed. Fire in parallel to
          // avoid serializing the two 800ms safety budgets, and AWAIT both
          // before finish()/navigate — otherwise React unmounts this page
          // before gtag processes the queued events and the beacons are
          // effectively lost (this is why events were missing in GA4 while
          // their dedup keys were being written to localStorage).
          //
          // The two trackXxx() Promises never reject; they resolve with
          // {delivered|dryRun|timeout}. If GA4 times out we proceed anyway,
          // and the retry-on-remount safety net kicks in because dedup is
          // now persisted only when result.delivered===true (see analytics.js).
          const trackingPromises = [trackSignUp({ signupKey: sid })];
          if (created?.stripe_subscription_id) {
            trackingPromises.push(trackStartTrial({
              subscriptionId: created.stripe_subscription_id,
              status: created.subscription_status,
            }));
          }
          await Promise.all(trackingPromises);
          if (cancelled) return;
          finish();
          return;
        }
        // Returning subscriber: just refresh subscription info.
        await api.get(`/stripe/session-status/${sid}`);
        const me = await api.get("/stripe/subscription-status");
        if (cancelled) return;
        if (refresh) refresh(me.data);
        // GA4 start_trial for the returning-subscriber path (e.g. resubscribe
        // that turned into a fresh trial). Same dedup key logic. Await so
        // the event is actually flushed before finish()/navigate.
        if (me.data?.stripe_subscription_id) {
          await trackStartTrial({
            subscriptionId: me.data.stripe_subscription_id,
            status: me.data.subscription_status,
          });
          if (cancelled) return;
        }
        if (me.data?.has_access) {
          finish();
        }
      } catch (err) {
        const detail = err.response?.data?.detail;
        // Backend says the session is not yet complete → keep polling.
        if (typeof detail === "string" && detail.includes("non è ancora stato completato")) {
          // ok, retry on next tick
        } else if (
          typeof detail === "string" &&
          (detail.includes("già stato utilizzato") || detail.includes("già completato"))
        ) {
          // Duplicate finalize: user was created by a previous tick. Just refresh.
          try {
            const me = await api.get("/stripe/subscription-status");
            if (!cancelled && refresh) refresh(me.data);
          } catch (_) { /* ignore */ }
          finish();
        } else if (err.response?.status && attemptsRef.current >= 8) {
          if (!cancelled) {
            setState("error");
            setMessage(typeof detail === "string" ? detail : "Impossibile finalizzare l'iscrizione.");
          }
          if (interval) clearInterval(interval);
          return;
        }
      } finally {
        inflightRef.current = false;
      }
      if (attemptsRef.current > 15 && !doneRef.current && !cancelled) {
        setState("timeout");
        if (interval) clearInterval(interval);
      }
    };

    tick();
    interval = setInterval(tick, 2000);
    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      if (redirectTimer) clearTimeout(redirectTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050B1F] text-white font-geist px-6 relative">
      <div className="absolute inset-0 auth-grid pointer-events-none opacity-40" />
      <div className="relative max-w-md w-full text-center" data-testid="subscribe-success-page">
        <div className="h-14 w-14 rounded-full bg-emerald-400/15 border border-emerald-400/30 flex items-center justify-center mx-auto mb-6">
          {state === "polling" ? (
            <Loader2 className="h-6 w-6 text-emerald-300 animate-spin" data-testid="subscribe-success-spinner" />
          ) : state === "error" ? (
            <span className="h-6 w-6 text-red-300">!</span>
          ) : (
            <Check className="h-7 w-7 text-emerald-300" strokeWidth={2.5} data-testid="subscribe-success-check" />
          )}
        </div>
        <div className="font-geist-mono text-[10px] uppercase tracking-widest text-[#4D8AFF] mb-3">
          {state === "polling" && "Elaborazione…"}
          {state === "ready" && "Iscrizione completata"}
          {state === "timeout" && "Elaborazione più lunga del previsto"}
          {state === "error" && "Errore"}
        </div>
        <h1 className="font-geist text-2xl md:text-3xl font-semibold text-white leading-tight">
          {state === "polling" && "Stiamo creando il tuo account…"}
          {state === "ready" && "Benvenuto in Revo PDF Loader!"}
          {state === "timeout" && "Il pagamento è in corso di verifica"}
          {state === "error" && (message || "Qualcosa è andato storto")}
        </h1>
        <p className="mt-3 font-geist-mono text-[11px] text-white/50 leading-relaxed">
          {state === "polling" && "L'account sarà attivo tra pochi secondi, dopo la conferma di Stripe."}
          {state === "ready" && "Ti stiamo portando all'editor…"}
          {state === "timeout" && "Aggiorna la pagina tra qualche istante — se il problema persiste, contatta il supporto."}
          {state === "error" && "Prova ad accedere. Se hai problemi, contatta il supporto e allega la session_id qui sopra."}
        </p>
        <Button
          data-testid="subscribe-success-cta"
          onClick={() => nav(state === "error" ? "/login" : "/", { replace: true })}
          disabled={state === "polling"}
          className="mt-8 h-11 px-6 rounded-sm bg-[#4D8AFF] hover:bg-[#75A6FF] text-[#050B1F] font-geist font-semibold disabled:opacity-40"
        >
          {state === "error" ? "Vai al login →" : "Vai all'editor →"}
        </Button>
      </div>
    </div>
  );
}
